/**
 * Catalog + defaults for the full-page instance create form.
 * Scopes align with SeedBaasPublishApiScopes / SeedClientDefaultInstanceScopes;
 * features with BaasApiInstanceFeatureCodes; Pix ceilings with PixLimitDefaults;
 * Stables with StableLimitDefaults; EFX with EfxLimitDefaults.
 */

import { formatInputValue } from "../../../../Utils/formatInputValue";

export const BAAS_NON_GRANTABLE_SCOPES = [
  "instances.create",
  "instances.program.create",
  "instances.owner.set",
  "instances.features.write",
  "instances.scopes.write",
  "instances.limits.write",
  "entities.features.write",
];

/** Partner scopes that identify Valut / global instance admins (not clients). */
export const BAAS_INSTANCE_ADMIN_SCOPES = [
  "instances.create",
  "instances.owner.set",
  "instances.scopes.write",
  "instances.features.write",
  "instances.limits.write",
];

/**
 * Default partner scopes for client-created instances (group “client-default”).
 * Keep aligned with SeedBaasPublishApiScopes.sql / SeedClientDefaultInstanceScopes.sql.
 * When the client omits `scopes` on POST /v1/instances, the API copies the parent’s
 * grantable set — seed this list on the client parent so defaults match.
 */
export const BAAS_CLIENT_DEFAULT_SCOPES = [
  "accounts.read",
  "transfers.write",
  "accounts.overdraft.read",
  "accounts.overdraft.fund",
  "fx.quotes.create",
  "fx.conversions.create",
  "fiat.markets.read",
  "pix.limits.read",
  "pix.limits.write",
  "stables.wallets.create",
  "stables.wallets.read",
  "stables.networks.read",
  "stables.markets.read",
  "stables.quotes.create",
  "stables.quotes.accept",
  "stables.addresses.validate",
  "stables.purchases.create",
  "stables.sales.create",
  "stables.withdrawals.create",
  "stables.conversions.create",
  "stables.transactions.read",
  "stables.limits.write",
  "instances.read",
  "instances.scopes.read",
  "instances.api_keys.read",
  "instances.api_keys.write",
  "instances.webhooks.read",
  "instances.webhooks.write",
  "instances.api_metrics.read",
  "instances.api_logs.read",
  "instances.pix_metrics.read",
];

/** Common BaaS publish scopes shown when parent catalog is empty / as suggested set. */
export const BAAS_PUBLISH_SCOPE_CATALOG = [...BAAS_CLIENT_DEFAULT_SCOPES];

/** Instance product gates (Recursos). Carteira IB is membership-based — not a toggle. */
export const BAAS_INSTANCE_FEATURE_OPTIONS = [
  { code: "pix", defaultEnabled: true },
  { code: "stables", defaultEnabled: true },
  { code: "fiat", defaultEnabled: true },
  { code: "two_factor", defaultEnabled: true },
  { code: "cards", defaultEnabled: false },
  { code: "accounts.cnr", defaultEnabled: false },
  { code: "kyc.veriff_session_create", defaultEnabled: false },
  { code: "kyc.invite_link", defaultEnabled: false },
  { code: "kyc.legal_entity_proposal_images", defaultEnabled: false },
  { code: "serpro.autocomplete", defaultEnabled: false },
];

/**
 * Partner scopes a cards-only instance still needs for Entidades, Emissor,
 * and PROGRAM_RESERVE shell funding — without Pix / Stables / FX.
 * Keep aligned with BaasApiInstanceFeatureCodes.CardsCoreScopes.
 */
export const BAAS_CARDS_CORE_SCOPES = [
  "entities.list",
  "entities.read",
  "entities.person.create",
  "entities.business.create",
  "entities.update",
  "entities.documents.upload",
  "accounts.read",
  "accounts.create",
  "accounts.overdraft.read",
  "accounts.overdraft.fund",
  "transfers.write",
  "cards.create",
  "cards.read",
  "cards.write",
  "instances.read",
];

/** Union cards-core scopes that exist on the parent catalog (never invent). */
export const withCardsCoreScopes = (selected = [], catalog = []) => {
  const catalogSet = new Set(
    (catalog || []).map((scope) => String(scope || "").trim()).filter(Boolean),
  );
  const next = new Set(
    (selected || []).map((scope) => String(scope || "").trim()).filter(Boolean),
  );
  for (const scope of BAAS_CARDS_CORE_SCOPES) {
    if (catalogSet.has(scope)) next.add(scope);
  }
  return Array.from(next);
};

/**
 * Account-holder (correntista) instance: entities + Valut account + transfers.
 * No Pix, Stables, FX, or cards — operator can still tick those individually.
 */
export const BAAS_CORRENTISTA_SCOPES = [
  "entities.list",
  "entities.read",
  "entities.person.create",
  "entities.business.create",
  "entities.update",
  "entities.documents.upload",
  "accounts.read",
  "accounts.create",
  "accounts.overdraft.read",
  "accounts.overdraft.fund",
  "transfers.write",
  "instances.read",
];

export const BAAS_SCOPE_PRESET_ISSUER = "issuer";
export const BAAS_SCOPE_PRESET_CORRENTISTA = "correntista";

/** Role chips on the instance scopes step / settings tab. */
export const BAAS_SCOPE_PRESETS = [
  {
    id: BAAS_SCOPE_PRESET_ISSUER,
    scopes: BAAS_CARDS_CORE_SCOPES,
    syncFeature: "cards",
  },
  {
    id: BAAS_SCOPE_PRESET_CORRENTISTA,
    scopes: BAAS_CORRENTISTA_SCOPES,
  },
];

/** Supported country rails exposed in the create wizard. */
export const BAAS_SUPPORTED_COUNTRY_OPTIONS = [
  { code: "BR", currency: "BRL" },
  { code: "US", currency: "USD" },
];

/** Default instance Pix ceilings (major BRL) — mirrors PixLimitDefaults.CatalogAmounts. */
export const BAAS_DEFAULT_PIX_LIMIT_ROWS = [
  { type: 1, code: "enviado", dayAmount: 5000, nightAmount: 1000 },
  { type: 2, code: "recebido", dayAmount: 10000, nightAmount: 5000 },
  { type: 3, code: "saque_troco", dayAmount: 3000, nightAmount: 1000 },
  { type: 4, code: "pj", dayAmount: 5000, nightAmount: 1000 },
  { type: 5, code: "agendado", dayAmount: 5000, nightAmount: 1000 },
  { type: 6, code: "automatico", dayAmount: 2000, nightAmount: 500 },
];

/**
 * Default instance Stables ceilings (major BRL) —
 * mirrors StableLimitDefaults / SeedStableLimitsAndFeesTest.sql (cents ÷ 100).
 * Daily + monthly so UTILIZADO matches calendar day / month (not hourly).
 */
export const BAAS_DEFAULT_STABLES_LIMIT_ROWS = [
  { operationType: "purchase", periodType: "daily", amount: 30000 },
  { operationType: "sale", periodType: "daily", amount: 25000 },
  { operationType: "conversion", periodType: "daily", amount: 20000 },
  { operationType: "withdrawal", periodType: "daily", amount: 10000 },
  { operationType: "purchase", periodType: "monthly", amount: 300000 },
  { operationType: "sale", periodType: "monthly", amount: 250000 },
  { operationType: "conversion", periodType: "monthly", amount: 200000 },
  { operationType: "withdrawal", periodType: "monthly", amount: 100000 },
];

/**
 * Default instance EFX ceilings (major BRL, hourly) —
 * mirrors EfxLimitDefaults (cents ÷ 100).
 */
export const BAAS_DEFAULT_EFX_LIMIT_ROWS = [
  { operationType: "conversion", periodType: "hourly", amount: 50000 },
  { operationType: "outbound", periodType: "hourly", amount: 30000 },
  { operationType: "inbound", periodType: "hourly", amount: 50000 },
];

export const BAAS_LIMIT_SECTION_TABS = [
  { id: "pix", labelKey: "baas.instances.createPage.limitsTabPix" },
  { id: "stables", labelKey: "baas.instances.createPage.limitsTabStables" },
  { id: "efx", labelKey: "baas.instances.createPage.limitsTabEfx" },
];

/** Rail selector for unified Limites + Tarifas settings screen. */
export const BAAS_RAIL_SECTION_TABS = BAAS_LIMIT_SECTION_TABS;

/**
 * Default instance PIX fees — enviado / recebido.
 * Mirrors PixFeeDefaults (0% / R$ 0 until configured). Overlay + Valut cost default to 0.
 * UI copy uses the instance name (not a branded rail example).
 */
export const BAAS_DEFAULT_PIX_FEE_ROWS = [
  { operationType: "enviado", feePercent: 0, feeFixed: 0, costPercent: 0, costFixed: 0 },
  { operationType: "recebido", feePercent: 0, feeFixed: 0, costPercent: 0, costFixed: 0 },
];

/** Wizard / missing name fallback for PIX cost-overlay copy (`{name} / 0`). */
export const PIX_FEE_INSTANCE_NAME_FALLBACK = "Instância";

/** Instance display name for Custo Valut / overlay examples (`Nome / 0`). */
export const resolvePixFeeInstanceName = (name, fallback = PIX_FEE_INSTANCE_NAME_FALLBACK) => {
  const trimmed = String(name ?? "").trim();
  return trimmed || fallback || PIX_FEE_INSTANCE_NAME_FALLBACK;
};

export const pixFeeFixedDraftKey = (operationType) =>
  `${String(operationType || "").toLowerCase()}__fixed`;

export const pixFeeCostPercentDraftKey = (operationType) =>
  `${String(operationType || "").toLowerCase()}__cost`;

export const pixFeeCostFixedDraftKey = (operationType) =>
  `${String(operationType || "").toLowerCase()}__costFixed`;

/** Draft keys for instance Pix ceilings (day/night amount, monthly, per-op). */
export const pixLimitDraftKey = (type, period, field = "amount") => {
  const base = `${Number(type)}-${Number(period)}`;
  if (field === "monthly") return `${base}-m`;
  if (field === "perop") return `${base}-p`;
  return base;
};

/**
 * Default instance EFX fees (%) —
 * mirrors EfxFeeDefaults / AccountFxService.DefaultFeePercent.
 */
export const BAAS_DEFAULT_EFX_FEE_ROWS = [
  { operationType: "conversion", feePercent: 0.35 },
  { operationType: "outbound", feePercent: 0.35 },
  { operationType: "inbound", feePercent: 0.35 },
];

/**
 * Default instance Stables operation fees (%) —
 * mirrors StableFeeCalculator.DefaultFeePercentForOperation.
 */
export const BAAS_DEFAULT_STABLES_FEE_ROWS = [
  { operationType: "purchase", feePercent: 0.5 },
  { operationType: "sale", feePercent: 0.5 },
  { operationType: "conversion", feePercent: 0.05 },
  { operationType: "withdrawal", feePercent: 0.5 },
];

/**
 * Default instance network fees (stable units) —
 * mirrors StableNetworkFeePolicyService.DefaultNetworkFeeForNetwork.
 */
export const BAAS_DEFAULT_NETWORK_FEE_ROWS = [
  { network: "POLYGON", feeAmountStable: 0.35 },
  { network: "ERC20", feeAmountStable: 2.5 },
  { network: "TRON", feeAmountStable: 1.0 },
  { network: "SOLANA", feeAmountStable: 0.01 },
];

export const BAAS_FEE_SECTION_TABS = [
  { id: "stables", labelKey: "baas.instances.createPage.feesTabStables" },
  { id: "network", labelKey: "baas.instances.createPage.feesTabNetwork" },
];

export const buildDefaultFeatureMap = () =>
  Object.fromEntries(
    BAAS_INSTANCE_FEATURE_OPTIONS.map((item) => [item.code, item.defaultEnabled]),
  );

/** Format major BRL as masked currency digits display (pt-BR, e.g. 5.000,00). */
export const formatBrlMajorMask = (major) => {
  const n = Number(major);
  if (!Number.isFinite(n) || n < 0) return "";
  return formatInputValue(String(Math.round(n * 100)));
};

/** Parse masked BRL display (or plain decimal) into major number. */
export const parseBrlMajorMask = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return NaN;
  const digits = raw.replace(/\D/g, "");
  if (!digits) return NaN;
  // Masked input is always cents-based when it contains grouping/decimal separators
  // or when produced by formatInputValue. Plain "5000" (no comma) stays major for safety
  // only when it has no separators — wizard always uses the mask.
  if (raw.includes(",") || raw.includes(".")) {
    return Number(digits) / 100;
  }
  // Digits-only from mask while typing still means cents once length implies decimals;
  // formatInputValue always writes with comma after first digit pair — treat all digit-only
  // from our drafts as cents when coming from onChange mask.
  return Number(digits) / 100;
};

export const buildDefaultPixLimitDrafts = () => {
  const drafts = {};
  for (const row of BAAS_DEFAULT_PIX_LIMIT_ROWS) {
    drafts[pixLimitDraftKey(row.type, 1)] = formatBrlMajorMask(row.dayAmount);
    drafts[pixLimitDraftKey(row.type, 2)] = formatBrlMajorMask(row.nightAmount);
    drafts[pixLimitDraftKey(row.type, 1, "monthly")] = formatBrlMajorMask(row.dayAmount * 20);
    drafts[pixLimitDraftKey(row.type, 2, "monthly")] = formatBrlMajorMask(row.nightAmount * 20);
    drafts[pixLimitDraftKey(row.type, 1, "perop")] = formatBrlMajorMask(row.dayAmount);
    drafts[pixLimitDraftKey(row.type, 2, "perop")] = formatBrlMajorMask(row.nightAmount);
  }
  return drafts;
};

/** Merge API Pix ceiling policies into drafts (defaults when missing). */
export const pixLimitDraftsFromPolicies = (items = []) => {
  const drafts = buildDefaultPixLimitDrafts();
  for (const item of items) {
    const type = Number(item?.type);
    const period = Number(item?.period);
    const amount = Number(item?.amount);
    if (!type || !period || !Number.isFinite(amount) || amount <= 0) continue;
    drafts[pixLimitDraftKey(type, period)] = formatBrlMajorMask(amount);
    const monthly = Number(item?.monthly_amount ?? item?.monthlyAmount);
    drafts[pixLimitDraftKey(type, period, "monthly")] = formatBrlMajorMask(
      Number.isFinite(monthly) && monthly > 0 ? monthly : amount * 20,
    );
    const perOp = Number(item?.per_operation_amount ?? item?.perOperationAmount);
    drafts[pixLimitDraftKey(type, period, "perop")] = formatBrlMajorMask(
      Number.isFinite(perOp) && perOp > 0 ? perOp : amount,
    );
  }
  return drafts;
};

export const stablesLimitDraftKey = (operationType, periodType = "hourly") =>
  `${String(operationType || "").toLowerCase()}-${String(periodType || "hourly").toLowerCase()}`;

export const buildDefaultStablesLimitDrafts = () => {
  const drafts = {};
  for (const row of BAAS_DEFAULT_STABLES_LIMIT_ROWS) {
    drafts[stablesLimitDraftKey(row.operationType, row.periodType)] = formatBrlMajorMask(row.amount);
  }
  return drafts;
};

export const buildDefaultEfxLimitDrafts = () => {
  const drafts = {};
  for (const row of BAAS_DEFAULT_EFX_LIMIT_ROWS) {
    drafts[stablesLimitDraftKey(row.operationType, row.periodType)] = formatBrlMajorMask(row.amount);
  }
  return drafts;
};

const pixPeriodPolicy = (drafts, type, period, hourStart, hourEnd) => {
  const amount = parseBrlMajorMask(drafts[pixLimitDraftKey(type, period)]);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const monthly = parseBrlMajorMask(drafts[pixLimitDraftKey(type, period, "monthly")]);
  const perOp = parseBrlMajorMask(drafts[pixLimitDraftKey(type, period, "perop")]);
  return {
    type,
    period,
    period_hour_start: hourStart,
    period_hour_end: hourEnd,
    amount,
    monthly_amount: Number.isFinite(monthly) && monthly > 0 ? monthly : amount * 20,
    per_operation_amount: Number.isFinite(perOp) && perOp > 0 ? perOp : amount,
  };
};

export const pixLimitPoliciesFromDrafts = (drafts = {}) => {
  const policies = [];
  for (const row of BAAS_DEFAULT_PIX_LIMIT_ROWS) {
    const day = pixPeriodPolicy(drafts, row.type, 1, 6, 19);
    const night = pixPeriodPolicy(drafts, row.type, 2, 20, 5);
    if (day) policies.push(day);
    if (night) policies.push(night);
  }
  return policies;
};

/** Convert major-BRL drafts → stables_limits API items (cents). */
export const stablesLimitPoliciesFromDrafts = (drafts = {}) => {
  const policies = [];
  for (const row of BAAS_DEFAULT_STABLES_LIMIT_ROWS) {
    const key = stablesLimitDraftKey(row.operationType, row.periodType);
    const major = parseBrlMajorMask(drafts[key]);
    if (Number.isFinite(major) && major > 0) {
      policies.push({
        operation_type: row.operationType,
        period_type: row.periodType,
        limit_amount_cents: Math.round(major * 100),
      });
    }
  }
  return policies;
};

/** Convert major-BRL drafts → efx_limits API items (cents). */
export const efxLimitPoliciesFromDrafts = (drafts = {}) => {
  const policies = [];
  for (const row of BAAS_DEFAULT_EFX_LIMIT_ROWS) {
    const key = stablesLimitDraftKey(row.operationType, row.periodType);
    const major = parseBrlMajorMask(drafts[key]);
    if (Number.isFinite(major) && major > 0) {
      policies.push({
        operation_type: row.operationType,
        period_type: row.periodType,
        limit_amount_cents: Math.round(major * 100),
      });
    }
  }
  return policies;
};

const formatFeePercentDraft = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "";
  return formatInputValue(String(Math.max(0, Math.round(n * 100))));
};

const parseFeePercentDraft = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return NaN;
  if (raw.includes(",")) return parseBrlMajorMask(raw);
  const n = Number(raw.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};

const formatNetworkFeeDraft = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "";
  return formatInputValue(String(Math.round(n * 100)));
};

const parseNetworkFeeDraft = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return NaN;
  if (raw.includes(",")) return parseBrlMajorMask(raw);
  const n = Number(raw.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};

export const buildDefaultStablesFeeDrafts = () => {
  const drafts = {};
  for (const row of BAAS_DEFAULT_STABLES_FEE_ROWS) {
    drafts[row.operationType] = formatFeePercentDraft(row.feePercent);
  }
  return drafts;
};

export const buildDefaultPixFeeDrafts = () => {
  const drafts = {};
  for (const row of BAAS_DEFAULT_PIX_FEE_ROWS) {
    drafts[row.operationType] = formatFeePercentDraft(row.feePercent);
    drafts[pixFeeFixedDraftKey(row.operationType)] = formatNetworkFeeDraft(row.feeFixed ?? 0);
    drafts[pixFeeCostPercentDraftKey(row.operationType)] = formatFeePercentDraft(row.costPercent ?? 0);
    drafts[pixFeeCostFixedDraftKey(row.operationType)] = formatNetworkFeeDraft(row.costFixed ?? 0);
  }
  return drafts;
};

export const buildDefaultEfxFeeDrafts = () => {
  const drafts = {};
  for (const row of BAAS_DEFAULT_EFX_FEE_ROWS) {
    drafts[row.operationType] = formatFeePercentDraft(row.feePercent);
  }
  return drafts;
};

export const buildDefaultNetworkFeeDrafts = () => {
  const drafts = {};
  for (const row of BAAS_DEFAULT_NETWORK_FEE_ROWS) {
    drafts[row.network] = formatNetworkFeeDraft(row.feeAmountStable);
  }
  return drafts;
};

/** Merge API fee policies into drafts (defaults when missing). */
export const stablesFeeDraftsFromPolicies = (policies = []) => {
  const drafts = buildDefaultStablesFeeDrafts();
  for (const item of policies || []) {
    const op = String(item?.operation_type || item?.operationType || "")
      .trim()
      .toLowerCase();
    if (!op || !Object.prototype.hasOwnProperty.call(drafts, op)) continue;
    const percent = Number(item?.fee_percent ?? item?.feePercent);
    if (Number.isFinite(percent) && percent >= 0) {
      drafts[op] = formatFeePercentDraft(percent);
    }
  }
  return drafts;
};

/** Merge API PIX fee policies into drafts (defaults when missing). */
export const pixFeeDraftsFromPolicies = (policies = []) => {
  const drafts = buildDefaultPixFeeDrafts();
  for (const item of policies || []) {
    const op = String(item?.operation_type || item?.operationType || "")
      .trim()
      .toLowerCase();
    if (!op || !Object.prototype.hasOwnProperty.call(drafts, op)) continue;
    const percent = Number(item?.fee_percent ?? item?.feePercent);
    if (Number.isFinite(percent) && percent >= 0) {
      drafts[op] = formatFeePercentDraft(percent);
    }
    const fixed = Number(item?.fee_fixed ?? item?.feeFixed);
    if (Number.isFinite(fixed) && fixed >= 0) {
      drafts[pixFeeFixedDraftKey(op)] = formatNetworkFeeDraft(fixed);
    }
    const costPercent = Number(item?.cost_percent ?? item?.costPercent);
    if (Number.isFinite(costPercent) && costPercent >= 0) {
      drafts[pixFeeCostPercentDraftKey(op)] = formatFeePercentDraft(costPercent);
    }
    const costFixed = Number(item?.cost_fixed ?? item?.costFixed);
    if (Number.isFinite(costFixed) && costFixed >= 0) {
      drafts[pixFeeCostFixedDraftKey(op)] = formatNetworkFeeDraft(costFixed);
    }
  }
  return drafts;
};

/** Merge API EFX fee policies into drafts (defaults when missing). */
export const efxFeeDraftsFromPolicies = (policies = []) => {
  const drafts = buildDefaultEfxFeeDrafts();
  for (const item of policies || []) {
    const op = String(item?.operation_type || item?.operationType || "")
      .trim()
      .toLowerCase();
    if (!op || !Object.prototype.hasOwnProperty.call(drafts, op)) continue;
    const percent = Number(item?.fee_percent ?? item?.feePercent);
    if (Number.isFinite(percent) && percent >= 0) {
      drafts[op] = formatFeePercentDraft(percent);
    }
  }
  return drafts;
};

/** Merge API network fees into drafts (defaults when missing). */
export const networkFeeDraftsFromPolicies = (networkFees = []) => {
  const drafts = buildDefaultNetworkFeeDrafts();
  for (const item of networkFees || []) {
    const network = String(item?.network || "")
      .trim()
      .toUpperCase();
    if (!network || !Object.prototype.hasOwnProperty.call(drafts, network)) continue;
    const amount = Number(item?.fee_amount_stable ?? item?.feeAmountStable);
    if (Number.isFinite(amount) && amount >= 0) {
      drafts[network] = formatNetworkFeeDraft(amount);
    }
  }
  return drafts;
};

/** Convert fee % drafts → stables/fees policies payload. */
export const stablesFeePoliciesFromDrafts = (drafts = {}) => {
  const policies = [];
  for (const row of BAAS_DEFAULT_STABLES_FEE_ROWS) {
    const percent = parseFeePercentDraft(drafts[row.operationType]);
    if (Number.isFinite(percent) && percent >= 0) {
      policies.push({
        operation_type: row.operationType,
        fee_percent: percent,
      });
    }
  }
  return policies;
};

/** Convert fee % drafts → pix/fees policies payload. */
export const pixFeePoliciesFromDrafts = (drafts = {}) => {
  const policies = [];
  for (const row of BAAS_DEFAULT_PIX_FEE_ROWS) {
    const percent = parseFeePercentDraft(drafts[row.operationType]);
    if (Number.isFinite(percent) && percent >= 0) {
      const fixed = parseNetworkFeeDraft(drafts[pixFeeFixedDraftKey(row.operationType)]);
      const costPercent = parseFeePercentDraft(drafts[pixFeeCostPercentDraftKey(row.operationType)]);
      const costFixed = parseNetworkFeeDraft(drafts[pixFeeCostFixedDraftKey(row.operationType)]);
      policies.push({
        operation_type: row.operationType,
        fee_percent: percent,
        fee_fixed: Number.isFinite(fixed) && fixed >= 0 ? fixed : 0,
        cost_percent: Number.isFinite(costPercent) && costPercent >= 0 ? costPercent : 0,
        cost_fixed: Number.isFinite(costFixed) && costFixed >= 0 ? costFixed : 0,
      });
    }
  }
  return policies;
};

/** Convert fee % drafts → efx/fees policies payload. */
export const efxFeePoliciesFromDrafts = (drafts = {}) => {
  const policies = [];
  for (const row of BAAS_DEFAULT_EFX_FEE_ROWS) {
    const percent = parseFeePercentDraft(drafts[row.operationType]);
    if (Number.isFinite(percent) && percent >= 0) {
      policies.push({
        operation_type: row.operationType,
        fee_percent: percent,
      });
    }
  }
  return policies;
};

/** Convert network fee drafts → stables/fees network_fees payload. */
export const networkFeePoliciesFromDrafts = (drafts = {}) => {
  const networkFees = [];
  for (const row of BAAS_DEFAULT_NETWORK_FEE_ROWS) {
    const amount = parseNetworkFeeDraft(drafts[row.network]);
    if (Number.isFinite(amount) && amount >= 0) {
      networkFees.push({
        network: row.network,
        fee_amount_stable: amount,
      });
    }
  }
  return networkFees;
};

export const isGrantableScope = (scopeName) => {
  const name = String(scopeName || "").trim();
  if (!name) return false;
  return !BAAS_NON_GRANTABLE_SCOPES.some(
    (blocked) => blocked.toLowerCase() === name.toLowerCase(),
  );
};

export const isInstanceCreateAdminFromScopes = (scopes = []) => {
  const set = new Set(
    (scopes || []).map((scope) => String(scope || "").trim().toLowerCase()).filter(Boolean),
  );
  return BAAS_INSTANCE_ADMIN_SCOPES.some((scope) => set.has(scope.toLowerCase()));
};

/** Caller partner key includes instances.owner.set (country / roots / KYC / currencies). */
export const hasInstancesOwnerSetScope = (scopes = []) => {
  const set = new Set(
    (scopes || []).map((scope) => String(scope || "").trim().toLowerCase()).filter(Boolean),
  );
  return set.has("instances.owner.set");
};

export const mergeScopeCatalog = (parentScopes = []) => {
  const merged = new Set();
  for (const scope of [...BAAS_PUBLISH_SCOPE_CATALOG, ...parentScopes]) {
    if (isGrantableScope(scope)) merged.add(String(scope).trim());
  }
  return Array.from(merged).sort((a, b) => a.localeCompare(b));
};

/** Preferred display order for scope prefix groups in the create wizard. */
export const BAAS_SCOPE_GROUP_ORDER = [
  "cards",
  "entities",
  "accounts",
  "pix",
  "stables",
  "fiat",
  "instances",
  "mobile",
  "other",
];

/**
 * Map a scope name → UI group key (first segment, with related prefixes merged).
 * e.g. accounts.read → accounts, fx.quotes.create → fiat, transfers.write → accounts.
 */
export const resolveScopeGroupKey = (scopeName) => {
  const name = String(scopeName || "").trim().toLowerCase();
  const prefix = (name.split(".")[0] || "").trim();
  if (!prefix) return "other";
  if (prefix === "fx" || prefix === "fiat") return "fiat";
  if (prefix === "transfers") return "accounts";
  if (prefix === "payments") return "pix";
  return prefix;
};

/** Group a flat catalog into sections by prefix for the Scopes wizard step. */
export const groupScopesByPrefix = (scopes = []) => {
  const buckets = new Map();
  for (const scope of scopes || []) {
    const name = String(scope || "").trim();
    if (!name) continue;
    const key = resolveScopeGroupKey(name);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(name);
  }

  for (const list of buckets.values()) {
    list.sort((a, b) => a.localeCompare(b));
  }

  return Array.from(buckets.keys())
    .sort((a, b) => {
      const ai = BAAS_SCOPE_GROUP_ORDER.indexOf(a);
      const bi = BAAS_SCOPE_GROUP_ORDER.indexOf(b);
      const ao = ai < 0 ? 999 : ai;
      const bo = bi < 0 ? 999 : bi;
      return ao - bo || a.localeCompare(b);
    })
    .map((key) => ({ key, scopes: buckets.get(key) }));
};

/** Grantable preset scopes that already exist on the parent catalog (never invent / elevate). */
export const resolvePresetScopes = (presetScopes = [], catalog = []) => {
  const catalogSet = new Set(
    (catalog || []).map((scope) => String(scope || "").trim()).filter(Boolean),
  );
  return (presetScopes || [])
    .map((scope) => String(scope || "").trim())
    .filter((scope) => scope && catalogSet.has(scope) && isGrantableScope(scope));
};

export const isScopePresetActive = (selected = [], catalog = [], presetScopes = []) => {
  const resolved = resolvePresetScopes(presetScopes, catalog);
  if (resolved.length === 0) return false;
  const selectedSet = new Set(
    (selected || []).map((scope) => String(scope || "").trim()).filter(Boolean),
  );
  return resolved.every((scope) => selectedSet.has(scope));
};

export const applyScopePreset = (selected = [], catalog = [], presetScopes = []) => {
  const next = new Set(
    (selected || []).map((scope) => String(scope || "").trim()).filter(Boolean),
  );
  for (const scope of resolvePresetScopes(presetScopes, catalog)) {
    next.add(scope);
  }
  return Array.from(next);
};

export const removeScopePreset = (
  selected = [],
  catalog = [],
  presetScopes = [],
  keepScopes = [],
) => {
  const resolved = new Set(resolvePresetScopes(presetScopes, catalog));
  const keep = new Set(
    (keepScopes || []).map((scope) => String(scope || "").trim()).filter(Boolean),
  );
  return (selected || [])
    .map((scope) => String(scope || "").trim())
    .filter((scope) => scope && (!resolved.has(scope) || keep.has(scope)));
};

/** Toggle a role preset. Other active presets keep their overlapping scopes. */
export const toggleScopePreset = (
  selected = [],
  catalog = [],
  preset,
  allPresets = BAAS_SCOPE_PRESETS,
) => {
  if (!preset) return [...(selected || [])];
  const active = isScopePresetActive(selected, catalog, preset.scopes);
  if (!active) {
    return applyScopePreset(selected, catalog, preset.scopes);
  }
  const keep = [];
  for (const other of allPresets || []) {
    if (!other || other.id === preset.id) continue;
    if (isScopePresetActive(selected, catalog, other.scopes)) {
      keep.push(...resolvePresetScopes(other.scopes, catalog));
    }
  }
  return removeScopePreset(selected, catalog, preset.scopes, keep);
};

/** Map selected country codes → API country_code + currencies. */
export const COUNTRY_CODE_ALIASES = {
  BR: "BR",
  BRA: "BR",
  BRASIL: "BR",
  BRAZIL: "BR",
  US: "US",
  USA: "US",
  EUA: "US",
  "UNITED STATES": "US",
};

export const normalizeSupportedCountryCode = (code) => {
  const raw = String(code || "").trim().toUpperCase();
  if (!raw) return "";
  if (COUNTRY_CODE_ALIASES[raw]) return COUNTRY_CODE_ALIASES[raw];
  return raw.length === 2 ? raw : "";
};

export const countriesFromInstanceDetail = (detail) => {
  const selected = new Set();
  const country = normalizeSupportedCountryCode(detail?.countryCode);
  const currencies = Array.isArray(detail?.supportedCurrencies)
    ? detail.supportedCurrencies
    : [];
  for (const item of BAAS_SUPPORTED_COUNTRY_OPTIONS) {
    const hasCurrency = currencies.some(
      (code) => String(code || "").trim().toUpperCase() === item.currency,
    );
    const hasCountry = currencies.some(
      (code) => normalizeSupportedCountryCode(code) === item.code,
    );
    if (hasCurrency || hasCountry) selected.add(item.code);
  }
  // CountryCode is a primary hint only when no currency allowlist is stored.
  if (selected.size === 0 && country) selected.add(country);
  return selected.size > 0 ? [...selected] : ["BR"];
};

export const resolveCountriesPayload = (countryCodes = []) => {
  const selected = new Set(
    (countryCodes || [])
      .map((code) => normalizeSupportedCountryCode(code))
      .filter(Boolean),
  );
  const currencies = BAAS_SUPPORTED_COUNTRY_OPTIONS.filter((item) => selected.has(item.code)).map(
    (item) => item.currency,
  );
  if (currencies.length === 0) {
    return { countryCode: "BR", currencies: ["BRL"] };
  }
  const primary = selected.has("BR") ? "BR" : Array.from(selected)[0];
  return { countryCode: primary, currencies };
};

export const buildWizardSteps = (includeAdminSteps) => {
  const steps = [
    { id: "basics", labelKey: "baas.instances.createPage.sectionBasics" },
    { id: "email", labelKey: "baas.instances.createPage.sectionEmail" },
    { id: "root", labelKey: "baas.instances.createPage.sectionRoot" },
    { id: "smtp", labelKey: "baas.instances.createPage.sectionSmtp" },
  ];
  if (includeAdminSteps) {
    steps.push({ id: "scopes", labelKey: "baas.instances.createPage.sectionScopes" });
  }
  steps.push({
    id: "permissions",
    labelKey: "baas.instances.createPage.sectionPermissions",
  });
  steps.push({ id: "review", labelKey: "baas.instances.createPage.sectionReview" });
  return steps;
};

/** Prefer parent RootAccountId, else transfer-options role=root (Valut matrix exit rail). */
export const resolveMatrixRootAccountId = (parentInstance, rootOptions = []) => {
  const fromParent = String(parentInstance?.rootAccountId || "").trim();
  if (fromParent) return fromParent;
  const roleRoot = (rootOptions || []).find(
    (option) => String(option?.role || "").toLowerCase() === "root" && option?.id,
  );
  return roleRoot?.id ? String(roleRoot.id).trim() : "";
};
